/**
 * GraceWindowBanner.jsx
 * CCH Care — Phase 2 (Shift-Based Access Control)
 * Last Modified: 2026-05-13
 *
 * Persistent banner shown when the user's shift has ended but is
 * still within the 2-hour late-documentation grace window.
 *
 * Per Policy v1.0 §4.3:
 *   "The user interface displays a persistent banner identifying that
 *    the shift has ended and stating the grace window expiration time."
 *
 * Pure presentational. Parent decides when to render this:
 *   - RequireOnShift renders it during the grace window for shift-gated routes
 *   - ShiftDashboard renders it directly (the dashboard route isn't shift-gated)
 *
 * Times are formatted in the browser's local time zone, which for CNAs
 * working at the facility matches the wall clock and the facility's TZ.
 */
import { COLORS } from '../../config/constants';

const LATE_DOC_GRACE_HOURS = 2; // matches LATE_DOC_GRACE_HOURS in cron + current.php

export default function GraceWindowBanner({ shift }) {
  if (!shift) return null;

  // Compute the shift's end timestamp in the browser's local interpretation.
  // For crosses_midnight (Night shift), the end_time belongs to the next
  // calendar day relative to shift_date.
  const endDT = new Date(`${shift.shift_date}T${shift.end_time}`);
  if (shift.crosses_midnight) {
    endDT.setDate(endDT.getDate() + 1);
  }

  const graceEndDT = new Date(endDT.getTime() + LATE_DOC_GRACE_HOURS * 60 * 60 * 1000);

  const fmtTime = (d) =>
    d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        // Amber palette — kept inline for now. Refactor to COLORS.warning*
        // tokens during the next dark-mode pass; the rest of the codebase
        // is migrating off hardcoded hex per the Phase 5f notes.
        background: '#FEF3C7',
        borderBottom: '1px solid #F59E0B',
        color: '#78350F',
        padding: '10px 16px',
        fontSize: 13,
        lineHeight: 1.4,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        position: 'sticky',
        top: 0,
        zIndex: 50,
      }}
    >
      <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>⏱</span>
      <div style={{ flex: 1 }}>
        <strong>Your shift ended at {fmtTime(endDT)}.</strong>{' '}
        Complete late documentation by <strong>{fmtTime(graceEndDT)}</strong>.
        After that, access locks and any further documentation requires a
        supervisor to reopen the record.
      </div>
    </div>
  );
}

// Suppress unused-import lint warning if COLORS isn't used after the
// dark-mode refactor lands. Importing it here keeps the file ready for
// that pass without requiring another import edit.
void COLORS;